import { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Storefront "Install this app" prompt.
 *
 * Chrome / Edge / Samsung Internet: browsers fire `beforeinstallprompt` when
 * the site meets PWA install criteria (manifest + SW + HTTPS). We capture that
 * event, hide the browser's default nag bar, and surface a friendlier pill on
 * our own UI. Tapping it fires the saved prompt so the customer sees the
 * native "Install app" dialog with the shop's name.
 *
 * iOS Safari: does NOT fire `beforeinstallprompt`. We detect iOS Safari and
 * show a small modal with the Share → Add to Home Screen instructions instead,
 * so those customers still know how to install.
 *
 * Session-persistent dismissal — a shopper who taps "Not now" won't be
 * prompted again in the same tab.
 */

const DISMISS_KEY = "shop_install_prompt_dismissed_v1";
const STANDALONE_KEY = "shop_pwa_installed_v1";

function isStandalone() {
  // Chrome / Android: matchMedia display-mode. Safari: window.navigator.standalone.
  if (typeof window === "undefined") return true;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  if (window.navigator?.standalone === true) return true;
  return false;
}

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return iOS && webkit;
}

/**
 * Header-icon variant of the install prompt. Always visible in the shop
 * header (unless already running standalone) — gives customers a discoverable
 * "install this app" entry point that doesn't rely on the browser's own
 * mini-infobar firing.
 */
export function InstallButton({ shopName = "" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return undefined; }
    const onBeforeInstall = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const install = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice.catch(() => null);
      } catch { /* older browsers */ }
      setDeferredPrompt(null);
      return;
    }
    // iOS Safari (no event) OR Chrome not-yet-eligible — show the how-to modal.
    setShowIosGuide(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={install}
        className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-100"
        title="Install this shop as an app"
        aria-label="Install app"
        data-testid="shop-install-header-btn"
      >
        <Download className="w-5 h-5" />
        <span className="hidden sm:inline text-sm">Install</span>
      </button>
      {showIosGuide && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosGuide(false)}
          data-testid="shop-install-manual-guide"
        >
          <div
            className="bg-white rounded-2xl p-5 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-semibold text-slate-900 mb-1">
              Install {shopName || "this shop"}
            </div>
            <div className="text-sm text-slate-600 mb-4">
              {isIosSafari()
                ? "On iPhone / iPad, Safari doesn't auto-prompt. Two quick taps:"
                : "Your browser will let you install this shop as an app. Look for one of these:"}
            </div>
            {isIosSafari() ? (
              <ol className="space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">1</span>
                  <span>Tap the <Share className="w-4 h-4 inline-block mx-0.5 text-blue-600" /> <b>Share</b> icon at the bottom of Safari.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">2</span>
                  <span>Scroll and tap <Plus className="w-4 h-4 inline-block mx-0.5" /> <b>Add to Home Screen</b>.</span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">1</span>
                  <span>Open your browser menu (three dots or hamburger icon in the address bar).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">2</span>
                  <span>Tap <b>Install app</b>, <b>Add to Home screen</b>, or <b>Create shortcut</b> — whichever your browser calls it.</span>
                </li>
                <li className="flex items-start gap-3 pt-1 border-t border-slate-100">
                  <span className="text-xs text-slate-500">
                    Some browsers only show the option after you&apos;ve visited the site a few times. Come back tomorrow and it should appear.
                  </span>
                </li>
              </ol>
            )}
            <button
              type="button"
              onClick={() => setShowIosGuide(false)}
              className="w-full mt-5 py-2 rounded-lg bg-[var(--shop-primary,#0055FF)] text-white font-semibold hover:opacity-90"
              data-testid="shop-install-manual-close"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function InstallPrompt({ shopName }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    // Already running as the installed PWA — nothing to do.
    if (isStandalone()) {
      try { sessionStorage.setItem(STANDALONE_KEY, "1"); } catch { /* ignore */ }
      return undefined;
    }
    if (sessionStorage.getItem(DISMISS_KEY)) return undefined;

    const onBeforeInstall = (e) => {
      // Suppress the mini-infobar; we render our own richer prompt instead.
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      try { sessionStorage.setItem(STANDALONE_KEY, "1"); } catch { /* ignore */ }
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari path — no event fires, but we still want to surface a hint
    // so customers know they *can* install. Delay so we don't punch a modal
    // in front of the page on first load.
    if (isIosSafari()) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  const install = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice.catch(() => null);
      } catch { /* Safari and older browsers may not support this */ }
      setDeferredPrompt(null);
      setVisible(false);
      return;
    }
    // iOS Safari — show the how-to modal.
    setShowIosGuide(true);
  };

  return (
    <>
      {/* Bottom-sheet install pill — floats above the storefront but below
          the cart FAB. Auto-hides on install / dismiss. */}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-sm w-[calc(100%-2rem)]"
        data-testid="shop-install-prompt"
      >
        <div className="bg-white shadow-xl border border-slate-200 rounded-2xl px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--shop-primary,#0055FF)] flex items-center justify-center shrink-0">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">
              Install {shopName || "this shop"}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              One-tap access from your home screen — no browser tabs.
            </div>
          </div>
          <Button
            size="sm"
            className="h-8 px-3 bg-[var(--shop-primary,#0055FF)] hover:opacity-90 text-white shrink-0"
            onClick={install}
            data-testid="shop-install-btn"
          >
            Install
          </Button>
          <button
            type="button"
            onClick={dismiss}
            className="text-slate-400 hover:text-slate-600 shrink-0 p-1"
            aria-label="Dismiss install prompt"
            data-testid="shop-install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* iOS Safari how-to modal — shown when the user taps Install on an
          iPhone/iPad since Safari won't do it automatically. */}
      {showIosGuide && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosGuide(false)}
          data-testid="shop-install-ios-guide"
        >
          <div
            className="bg-white rounded-2xl p-5 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-semibold text-slate-900 mb-1">
              Install {shopName || "this shop"} on iPhone
            </div>
            <div className="text-sm text-slate-600 mb-4">
              Safari doesn&apos;t have a one-tap install button — it&apos;s two quick taps:
            </div>
            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">1</span>
                <span>
                  Tap the <Share className="w-4 h-4 inline-block mx-0.5 text-blue-600" /> <b>Share</b> icon at the bottom of Safari.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">2</span>
                <span>
                  Scroll down and tap <Plus className="w-4 h-4 inline-block mx-0.5" /> <b>Add to Home Screen</b>.
                </span>
              </li>
            </ol>
            <Button
              className="w-full mt-5 bg-[var(--shop-primary,#0055FF)] hover:opacity-90 text-white"
              onClick={() => { setShowIosGuide(false); dismiss(); }}
              data-testid="shop-install-ios-close"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
